import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Cable,
  CheckCircle2,
  Clock3,
  Database,
  LoaderCircle,
  Play,
  Power,
  RefreshCw,
  Save,
  ShieldCheck,
  Unplug,
} from "lucide-react";
import type { AdminConnectivityView, MysqlConfigurationInput } from "@ubisafe/shared";
import { ApiClientError, api } from "../lib/api";

interface ConnectivityPageProps {
  onStatusChange?: (status: AdminConnectivityView) => void;
}

type ActionName = "connect" | "disconnect" | "mysql" | "test" | "sync" | "refresh";

const vpnLabels = {
  UNCONFIGURED: "Não configurada",
  DISCONNECTED: "Desconectada",
  CONNECTING: "Conectando",
  CONNECTED: "Conectada",
  ERROR: "Com falha",
} as const;

const mysqlLabels = {
  UNCONFIGURED: "Não configurado",
  UNAVAILABLE: "Indisponível",
  AVAILABLE: "Disponível",
  ERROR: "Com falha",
} as const;

const syncLabels = {
  QUEUED: "Aguardando execução",
  RUNNING: "Sincronizando",
  SUCCEEDED: "Concluída",
  FAILED: "Falhou",
} as const;

function formatDate(value: string | null): string {
  if (!value) return "Ainda não disponível";
  return new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function attentionRequired(status: AdminConnectivityView): boolean {
  return status.vpn.status !== "CONNECTED" || status.mysql.status !== "AVAILABLE";
}

export function ConnectivityPage({ onStatusChange }: ConnectivityPageProps) {
  const [status, setStatus] = useState<AdminConnectivityView | null>(null);
  const [totp, setTotp] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<ActionName | null>(null);
  const [editingMysql, setEditingMysql] = useState(false);
  const [mysqlForm, setMysqlForm] = useState<MysqlConfigurationInput>({
    host: "",
    port: 3306,
    customerDatabase: "customer",
    authDatabase: "auth",
    username: "",
    password: "",
  });

  const publish = useCallback(
    (next: AdminConnectivityView) => {
      setStatus(next);
      onStatusChange?.(next);
      if (next.mysql.summary) {
        setMysqlForm((current) => ({
          host: next.mysql.summary!.host,
          port: next.mysql.summary!.port,
          customerDatabase: next.mysql.summary!.customerDatabase,
          authDatabase: next.mysql.summary!.authDatabase,
          username: next.mysql.summary!.username,
          password: current.password,
        }));
      }
    },
    [onStatusChange],
  );

  const refresh = useCallback(
    async (silent = false) => {
      if (!silent) setActiveAction("refresh");
      try {
        publish(await api.getConnectivity());
      } catch (caught) {
        if (!silent) setError(caught instanceof Error ? caught.message : "Não foi possível consultar o estado.");
      } finally {
        if (!silent) setActiveAction(null);
      }
    },
    [publish],
  );

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(true), 5_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const run = async (action: ActionName, operation: () => Promise<AdminConnectivityView>, success: string) => {
    setActiveAction(action);
    setMessage(null);
    setError(null);
    try {
      publish(await operation());
      setMessage(success);
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : "Não foi possível concluir a operação.");
    } finally {
      setActiveAction(null);
    }
  };

  const handleConnect = async (event: React.FormEvent) => {
    event.preventDefault();
    await run("connect", () => api.connectVpn(totp), "Conexão privada estabelecida e validação MySQL iniciada.");
    setTotp("");
  };

  const handleMysqlSave = async (event: React.FormEvent) => {
    event.preventDefault();
    await run("mysql", () => api.configureMysql(mysqlForm), "Acesso às duas bases validado e sincronização enfileirada.");
    setMysqlForm((current) => ({ ...current, password: "" }));
    setEditingMysql(false);
  };

  const syncBlocked =
    !status ||
    status.vpn.status !== "CONNECTED" ||
    status.mysql.status !== "AVAILABLE" ||
    status.synchronization.latestRun?.status === "QUEUED" ||
    status.synchronization.latestRun?.status === "RUNNING";

  const statusSummary = useMemo(() => {
    if (!status) return "Carregando";
    return attentionRequired(status) ? "Atenção necessária" : "Operação disponível";
  }, [status]);

  if (!status) {
    return (
      <section className="page-state" aria-live="polite">
        <LoaderCircle className="spin" aria-hidden="true" />
        <strong>Consultando conectividade</strong>
        <span>Carregando o estado protegido do servidor.</span>
      </section>
    );
  }

  const latestRun = status.synchronization.latestRun;

  return (
    <div className="page-stack connectivity-page">
      <header className="page-heading page-heading--with-actions">
        <div>
          <div className="eyebrow">Administração técnica</div>
          <h1>Conectividade e sincronização</h1>
          <p>Restabeleça o acesso privado, valide as bases MySQL e acompanhe a atualização dos tenants.</p>
        </div>
        <button className="button button--secondary" onClick={() => void refresh()} disabled={activeAction !== null}>
          <RefreshCw className={activeAction === "refresh" ? "spin" : ""} aria-hidden="true" />
          Atualizar estado
        </button>
      </header>

      <div className={`system-summary ${attentionRequired(status) ? "system-summary--attention" : "system-summary--ok"}`}>
        {attentionRequired(status) ? <AlertCircle aria-hidden="true" /> : <ShieldCheck aria-hidden="true" />}
        <div>
          <strong>{statusSummary}</strong>
          <span>
            {attentionRequired(status)
              ? "Siga as etapas abaixo para liberar a sincronização e os serviços dependentes."
              : "Conexão privada e réplica MySQL estão disponíveis."}
          </span>
        </div>
      </div>

      {message && <div className="feedback feedback--success" role="status"><CheckCircle2 aria-hidden="true" />{message}</div>}
      {error && <div className="feedback feedback--error" role="alert"><AlertCircle aria-hidden="true" />{error}</div>}

      <section className="connectivity-grid" aria-label="Estado das integrações">
        <article className="connectivity-card">
          <div className="connectivity-card__header">
            <div className="connectivity-card__icon"><Cable aria-hidden="true" /></div>
            <div>
              <span>Conexão privada</span>
              <strong>{vpnLabels[status.vpn.status]}</strong>
            </div>
            <span className={`status-dot status-dot--${status.vpn.status.toLowerCase()}`} />
          </div>
          <dl className="detail-list">
            <div><dt>Última verificação</dt><dd>{formatDate(status.vpn.checkedAt)}</dd></div>
            <div><dt>Conectada desde</dt><dd>{formatDate(status.vpn.connectedAt)}</dd></div>
          </dl>
        </article>

        <article className="connectivity-card">
          <div className="connectivity-card__header">
            <div className="connectivity-card__icon"><Database aria-hidden="true" /></div>
            <div>
              <span>Réplica MySQL</span>
              <strong>{mysqlLabels[status.mysql.status]}</strong>
            </div>
            <span className={`status-dot status-dot--${status.mysql.status.toLowerCase()}`} />
          </div>
          <dl className="detail-list">
            <div><dt>Último teste bem-sucedido</dt><dd>{formatDate(status.mysql.lastSucceededAt)}</dd></div>
            <div><dt>Bases esperadas</dt><dd>{status.mysql.summary ? `${status.mysql.summary.authDatabase} e ${status.mysql.summary.customerDatabase}` : "auth e customer"}</dd></div>
          </dl>
        </article>

        <article className="connectivity-card">
          <div className="connectivity-card__header">
            <div className="connectivity-card__icon"><Clock3 aria-hidden="true" /></div>
            <div>
              <span>Sincronização</span>
              <strong>{latestRun ? syncLabels[latestRun.status] : "Ainda não executada"}</strong>
            </div>
          </div>
          <dl className="detail-list">
            <div><dt>Último snapshot MySQL</dt><dd>{formatDate(status.synchronization.latestSnapshotAt)}</dd></div>
            <div><dt>Próxima execução</dt><dd>{formatDate(status.synchronization.nextScheduledAt)}</dd></div>
          </dl>
        </article>
      </section>

      <section className="workflow-panel">
        <div className="workflow-step">
          <div className="workflow-step__number">1</div>
          <div className="workflow-step__body">
            <div className="workflow-step__heading">
              <div><span>Conexão privada</span><h2>Restabelecer conectividade</h2></div>
              {status.vpn.status === "CONNECTED" && <CheckCircle2 className="workflow-step__done" aria-label="Concluído" />}
            </div>
            {status.vpn.status === "CONNECTED" ? (
              <div className="inline-action">
                <p>A conexão está ativa. O código temporário utilizado não foi armazenado.</p>
                <button className="button button--danger-ghost" onClick={() => void run("disconnect", api.disconnectVpn, "Conexão privada encerrada.")} disabled={activeAction !== null}>
                  <Unplug aria-hidden="true" /> Desconectar
                </button>
              </div>
            ) : (
              <form className="totp-form" onSubmit={(event) => void handleConnect(event)}>
                <label className="field">
                  <span>Código temporário</span>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={totp}
                    onChange={(event) => setTotp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    aria-describedby="totp-help"
                    required
                  />
                </label>
                <p id="totp-help" className="field-help">O código é enviado ao agente somente durante esta tentativa e descartado em seguida.</p>
                <button className="button button--primary" type="submit" disabled={totp.length !== 6 || activeAction !== null}>
                  {activeAction === "connect" ? <LoaderCircle className="spin" aria-hidden="true" /> : <Power aria-hidden="true" />}
                  Conectar
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="workflow-step">
          <div className="workflow-step__number">2</div>
          <div className="workflow-step__body">
            <div className="workflow-step__heading">
              <div><span>Réplica de produção</span><h2>Validar acesso MySQL</h2></div>
              {status.mysql.status === "AVAILABLE" && <CheckCircle2 className="workflow-step__done" aria-label="Concluído" />}
            </div>

            {status.mysql.summary && !editingMysql ? (
              <div className="configured-credential">
                <dl className="detail-list detail-list--columns">
                  <div><dt>Servidor</dt><dd>{status.mysql.summary.host}:{status.mysql.summary.port}</dd></div>
                  <div><dt>Usuário</dt><dd>{status.mysql.summary.username}</dd></div>
                  <div><dt>Senha</dt><dd>Protegida e não exibida</dd></div>
                  <div><dt>Bases</dt><dd>{status.mysql.summary.authDatabase} / {status.mysql.summary.customerDatabase}</dd></div>
                </dl>
                <div className="button-row">
                  <button className="button button--secondary" onClick={() => setEditingMysql(true)} disabled={activeAction !== null}>Atualizar credenciais</button>
                  <button className="button button--secondary" onClick={() => void run("test", api.testMysql, "Acesso às duas bases validado.")} disabled={status.vpn.status !== "CONNECTED" || activeAction !== null}>
                    {activeAction === "test" ? <LoaderCircle className="spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />} Testar novamente
                  </button>
                </div>
              </div>
            ) : (
              <form className="mysql-form" onSubmit={(event) => void handleMysqlSave(event)}>
                <label className="field field--wide"><span>Servidor</span><input value={mysqlForm.host} onChange={(event) => setMysqlForm({ ...mysqlForm, host: event.target.value })} required /></label>
                <label className="field"><span>Porta</span><input type="number" min={1} max={65535} value={mysqlForm.port} onChange={(event) => setMysqlForm({ ...mysqlForm, port: Number(event.target.value) })} required /></label>
                <label className="field"><span>Usuário</span><input value={mysqlForm.username} onChange={(event) => setMysqlForm({ ...mysqlForm, username: event.target.value })} required /></label>
                <label className="field"><span>Base de autenticação</span><input value={mysqlForm.authDatabase} onChange={(event) => setMysqlForm({ ...mysqlForm, authDatabase: event.target.value })} required /></label>
                <label className="field"><span>Base de clientes</span><input value={mysqlForm.customerDatabase} onChange={(event) => setMysqlForm({ ...mysqlForm, customerDatabase: event.target.value })} required /></label>
                <label className="field field--wide"><span>Senha</span><input type="password" autoComplete="new-password" value={mysqlForm.password} onChange={(event) => setMysqlForm({ ...mysqlForm, password: event.target.value })} required /></label>
                <div className="button-row field--wide">
                  <button className="button button--primary" type="submit" disabled={status.vpn.status !== "CONNECTED" || activeAction !== null}>
                    {activeAction === "mysql" ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />} Validar e salvar
                  </button>
                  {status.mysql.summary && <button className="button button--secondary" type="button" onClick={() => setEditingMysql(false)}>Cancelar</button>}
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="workflow-step">
          <div className="workflow-step__number">3</div>
          <div className="workflow-step__body">
            <div className="workflow-step__heading">
              <div><span>Snapshot integral</span><h2>Atualizar tenants</h2></div>
              {latestRun?.status === "SUCCEEDED" && <CheckCircle2 className="workflow-step__done" aria-label="Concluído" />}
            </div>
            <p>A aplicação publica o novo conjunto somente após concluir a consulta inteira; em caso de falha, o snapshot anterior permanece visível.</p>
            {latestRun && (
              <div className="sync-run-summary">
                <span>{syncLabels[latestRun.status]}</span>
                <strong>{latestRun.tenantCount === null ? "—" : `${latestRun.tenantCount} tenants`}</strong>
                <small>Solicitada em {formatDate(latestRun.requestedAt)}</small>
              </div>
            )}
            <button className="button button--primary" onClick={() => void run("sync", api.synchronizeNow, "Sincronização enfileirada.")} disabled={syncBlocked || activeAction !== null}>
              {activeAction === "sync" ? <LoaderCircle className="spin" aria-hidden="true" /> : <Play aria-hidden="true" />} Sincronizar agora
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
